/**
 * The dossier's correction log, as the patient page received it, and the row behind a gesture it
 * shows. Provided by the patient page; `null` elsewhere, where a modal opens blank as it always did.
 */
import { createContext, useContext } from "react";
import type { MageXFeedback } from "@api/src/types/patient";
import type { ServedGesture } from "@app/utils/resolve-field-value";
import type { RevisedCorrection } from "@app/utils/correction-gesture";
import type {
  CorrectionErrorClass,
  JustificationSource,
} from "@app/types/correction";

export const CorrectionLogContext = createContext<MageXFeedback[] | null>(null);

export function useCorrectionLog(): MageXFeedback[] | null {
  return useContext(CorrectionLogContext);
}

const ERROR_CLASSES: readonly string[] = [
  "MISSED_IN_SOURCE",
  "MISREAD_SOURCE",
  "WRONG_FIELD",
  "OVER_EXTRACTED",
  "VOCABULARY",
  "ABSENT_FROM_SOURCE",
  "MISATTRIBUTED",
  "CLINICAL_JUDGEMENT",
] satisfies readonly CorrectionErrorClass[];
const SOURCES: readonly string[] = [
  "auto",
  "confirmed",
  "overridden",
  "manual",
] satisfies readonly JustificationSource[];

const TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?/;

// Microseconds since the epoch of a naive UTC timestamp, as both sides serialise one.
const microseconds = (iso: string): number | null => {
  const m = TIMESTAMP.exec(iso);
  if (!m) return null;
  const [, y, mo, d, h, mi, s, fraction] = m;
  const ms = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
  return ms * 1000 + Number((fraction ?? "").padEnd(6, "0"));
};

/**
 * Whether two timestamps name the same submission. The rule of `gesture_log.GestureLog.behind`:
 * the log stores the time to the millisecond, rounded, while the journal row that carries the
 * gesture was named with microseconds.
 */
export function sameSubmission(a: string, b: string): boolean {
  const ta = microseconds(a);
  const tb = microseconds(b);
  return ta !== null && tb !== null && Math.abs(ta - tb) < 1000;
}

/** The log row of *field* behind *gesture*, or `null` when the log holds none. */
export function loggedRow(
  log: readonly MageXFeedback[] | null,
  field: string,
  gesture: ServedGesture | null,
): MageXFeedback | null {
  const submitted = gesture?.submitted_at;
  if (!log || !submitted) return null;
  return (
    log.find(
      (row) =>
        row.field === field &&
        Boolean(row.created_at) &&
        sameSubmission(row.created_at, submitted),
    ) ?? null
  );
}

/**
 * The correction a reopened modal revises, from its log row: only a correction or a flag, only
 * one whose verdict is the one the row shows, and only while the value on screen is still the one
 * it judged.
 */
export function revisedCorrection(
  row: MageXFeedback | null,
  gesture: ServedGesture | null,
  reader: { id?: string | null; email?: string | null } | null,
): RevisedCorrection | null {
  if (!row || !gesture || gesture.superseded) return null;
  const verdict = row.gesture;
  if (verdict !== gesture.verdict) return null;
  if (verdict !== "corrected" && verdict !== "flagged") return null;
  const errorClass =
    row.error_class && ERROR_CLASSES.includes(row.error_class)
      ? (row.error_class as CorrectionErrorClass)
      : null;
  const source =
    row.justification_source && SOURCES.includes(row.justification_source)
      ? (row.justification_source as JustificationSource)
      : null;
  return {
    verdict,
    replacedRaw: row.old_value,
    replacedValue: row.replaced_value,
    errorClass,
    justificationSource: source,
    notes: row.notes ?? "",
    targetField: row.target_field ?? null,
    // The day the server anchored it on: its own date, else the day it was saved.
    anchor: (
      gesture.effective_date ??
      row.effective_date ??
      row.created_at
    ).slice(0, 10),
    scopeDocId: row.scope_doc_id ?? null,
    madeAt: row.created_at,
    authorName: row.author_name ?? null,
    authorIsDoctor: row.author_role === "medecin",
    byReader:
      Boolean(row.created_by) &&
      (row.created_by === reader?.id || row.created_by === reader?.email),
  };
}
